import { consent_api } from '@axo/shared/data-access/api';
import { useTranslationContext } from '@axo/shared/i18n';
import { Locales } from 'libs/shared/i18n/src/lib/types';
import { useCallback, useEffect, useState } from 'react';
import { useAxoFormContext } from '../../../context/AxoForm/AxoForm';

export type ConsentConfig = {
  loading?: boolean;
  content: string;
  partners?: string[];
  services?: string[];
  ConfigID: number;
  Lang: Locales;
};

export const useConsentConfig = (external: boolean) => {
  const { language } = useTranslationContext();
  const { baseUrl, marketCountry, step } = useAxoFormContext();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ConsentConfig | undefined>();

  const getConsentConfig = useCallback(async () => {
    setLoading(true);

    const config = await consent_api.getConsentConfig(baseUrl)(marketCountry);
    if (!config) {
      throw new Error(`No consent config found for ${marketCountry}`);
    }

    const content = config[`Text${marketCountry}`];

    const { partners, services } = config.Partners.reduce(
      (acc: { partners: string[]; services: string[] }, curr) => {
        if (curr.BrandID === 'service') {
          acc.services.push(curr.Name);
        } else {
          acc.partners.push(curr.Name);
        }

        return acc;
      },
      { partners: [], services: [] }
    );

    const consentData = {
      content,
      partners,
      services,
      ConfigID: config.ID,
      OriginAction: `Step ${step}`,
      Lang: language,
      Country: marketCountry,
    };

    setData(consentData);

    setLoading(false);
  }, [baseUrl, language, marketCountry, step]);

  useEffect(() => {
    let ignore = false;

    if (ignore || data) return;

    external && getConsentConfig();

    return () => {
      ignore = true;
    };
  }, [data, getConsentConfig, external]);

  return {
    loading,
    data,
  };
};
